/**
 * Save / Load — v0M15.1.
 *
 * Serialises a live `GameState` (plus the original `Scenario` needed to
 * reconstruct `SimContext` on the load path) to a JSON string that
 * round-trips through `deserialiseSavedGame()` losslessly.
 *
 * Design choices (v0M15-design lock):
 *
 * 1. **JSON with typed sentinels for non-JSON-native values.**
 *    - `Map<K, V>` → `{ __t: 'map', e: [[k, v], ...] }` — replacer walks
 *      every nested Map (there are many: `state.factions`, .provinces,
 *      .settlements, .armies, .commanders, .cohorts, .spies (optional),
 *      .worldStructures (optional), .battles, .visibility (optional),
 *      plus battle.sides[i].cohorts and battle.terrain.wallHp).
 *    - `Uint8Array` → `{ __t: 'u8', b: '<base64>' }` — `VisibilityMap.cells`
 *      is a Uint8Array (row-major cell-state bytes). Base64 keeps the
 *      save small; a 480×280 map's per-faction visibility layer is
 *      134,400 bytes → ~180 kB base64. Six factions = ~1 MB visibility
 *      total, comfortably inside the localStorage ~5 MB / origin cap.
 *
 * 2. **Schema version stamped at the top level.** Bump when incompatible
 *    changes land; deserialise refuses mismatched saves with a clear
 *    error rather than silently loading a bad shape. Migration hooks can
 *    slot in later if it becomes worth the maintenance cost — for a
 *    hobby-scale project a hard mismatch is honest and simple.
 *
 * 3. **Scenario is saved alongside GameState.** On the load path we call
 *    `bootstrapScenario()` to rebuild `SimContext.mapData` + the initial
 *    frame, then overwrite the returned `state` with the saved one. The
 *    scenario captures the seed + map identity + faction slots + victory
 *    condition — enough to reconstruct any context the sim needs beyond
 *    the raw entity Maps.
 *
 * 4. **Client-side state (camera, selection, opened panels, HUD mode) is
 *    NOT saved.** Save round-trip is pure sim. Load starts fresh on the
 *    client with the sim's tick + entity data restored. If the player
 *    wants to remember their camera position mid-save that's a v0M15+
 *    polish candidate.
 *
 * 5. **Branded types are erased at runtime.** All the FactionId / ArmyId
 *    / CohortId / SettlementId / etc. brands are compile-time markers on
 *    plain numbers; JSON serialises them as raw numbers and deserialise
 *    just casts back. No runtime cost.
 */

import type { GameState } from './state';
import type { Scenario } from './scenario';

/**
 * Schema version. Bump when GameState / Scenario / any nested type
 * gains a required field or changes an existing field's shape in a way
 * that would break a naive re-hydrate.
 */
export const SAVE_SCHEMA_VERSION = 1;

/**
 * Top-level save wrapper. Slot metadata (name, savedAt, thumbnail) is
 * added by the storage layer at v0M15.2; the sim-side save.ts is
 * concerned only with the sim + scenario payload.
 */
export interface SavedGame {
  readonly schemaVersion: number;
  readonly scenario: Scenario;
  readonly state: GameState;
}

/**
 * Serialise a save-game to a JSON string. Round-trips losslessly with
 * `deserialiseSavedGame()`. Throws only on internal errors (Map with
 * non-serialisable keys, cyclic references — neither of which the sim
 * currently produces).
 */
export function serialiseSavedGame(saved: SavedGame): string {
  return JSON.stringify(saved, replacer);
}

/**
 * Parse a JSON save-game string back to a `SavedGame`. Validates the
 * schema version + basic shape; throws with a diagnostic message on
 * mismatch or malformed input.
 */
export function deserialiseSavedGame(json: string): SavedGame {
  let raw: unknown;
  try {
    raw = JSON.parse(json, reviver);
  } catch (err) {
    throw new Error(`save.deserialiseSavedGame: JSON parse failed — ${err instanceof Error ? err.message : String(err)}`);
  }
  if (!raw || typeof raw !== 'object') {
    throw new Error(`save.deserialiseSavedGame: top-level payload is not an object`);
  }
  const obj = raw as Record<string, unknown>;
  const v = obj['schemaVersion'];
  if (typeof v !== 'number') {
    throw new Error(`save.deserialiseSavedGame: missing or non-numeric schemaVersion`);
  }
  if (v !== SAVE_SCHEMA_VERSION) {
    throw new Error(
      `save.deserialiseSavedGame: schema version mismatch — save is v${v}, current is v${SAVE_SCHEMA_VERSION}`,
    );
  }
  if (!obj['scenario'] || typeof obj['scenario'] !== 'object') {
    throw new Error(`save.deserialiseSavedGame: missing scenario`);
  }
  if (!obj['state'] || typeof obj['state'] !== 'object') {
    throw new Error(`save.deserialiseSavedGame: missing state`);
  }
  return obj as unknown as SavedGame;
}

// ---------------------------------------------------------------------------
// JSON replacer / reviver — Map + Uint8Array handling
// ---------------------------------------------------------------------------

/**
 * Sentinel-tagged shape for a serialised Map. Chosen so the reviver can
 * distinguish it unambiguously from any plain object the sim might carry
 * (no sim object uses `__t` as a field name).
 */
interface SerialisedMap {
  readonly __t: 'map';
  readonly e: readonly [unknown, unknown][];
}

/** Sentinel-tagged shape for a serialised Uint8Array. */
interface SerialisedU8 {
  readonly __t: 'u8';
  readonly b: string; // base64
}

function replacer(_key: string, value: unknown): unknown {
  if (value instanceof Map) {
    const entries: [unknown, unknown][] = [];
    for (const [k, v] of value.entries()) {
      entries.push([k, v]);
    }
    const out: SerialisedMap = { __t: 'map', e: entries };
    return out;
  }
  if (value instanceof Uint8Array) {
    const out: SerialisedU8 = { __t: 'u8', b: u8ToBase64(value) };
    return out;
  }
  return value;
}

function reviver(_key: string, value: unknown): unknown {
  if (value && typeof value === 'object' && '__t' in value) {
    const tag = (value as { __t: unknown }).__t;
    if (tag === 'map') {
      const entries = (value as SerialisedMap).e;
      const m = new Map();
      for (const [k, v] of entries) {
        m.set(k, v);
      }
      return m;
    }
    if (tag === 'u8') {
      return base64ToU8((value as SerialisedU8).b);
    }
  }
  return value;
}

// ---------------------------------------------------------------------------
// Base64 codec for Uint8Array (browser + Node friendly)
// ---------------------------------------------------------------------------

/**
 * Encode a Uint8Array to a base64 string. Uses `btoa` in browsers; falls
 * back to Node's Buffer in tests / Node runtime.
 */
function u8ToBase64(u8: Uint8Array): string {
  if (typeof btoa === 'function') {
    // btoa expects a binary string. For large arrays, chunk the
    // fromCharCode call so we don't blow the argument-count limit.
    const CHUNK = 0x8000;
    let bin = '';
    for (let i = 0; i < u8.length; i += CHUNK) {
      const end = Math.min(i + CHUNK, u8.length);
      bin += String.fromCharCode(...u8.subarray(i, end));
    }
    return btoa(bin);
  }
  // Node fallback (tests).
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  return Buffer.from(u8).toString('base64');
}

/**
 * Decode a base64 string to a Uint8Array. Uses `atob` in browsers; falls
 * back to Node's Buffer.
 */
function base64ToU8(b64: string): Uint8Array {
  if (typeof atob === 'function') {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      out[i] = bin.charCodeAt(i);
    }
    return out;
  }
  // Node fallback (tests).
  return new Uint8Array(Buffer.from(b64, 'base64'));
}
